import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { activitiesKey, liveScope, screenCodeKey, smsKey, smsPageKey } from "./channel";
import type { SmsInbox, SmsPage, SmsPaging, SmsReplay } from "./sms";
import type {
  Activity,
  ActivityType,
  LivePayload,
  LiveState,
  Results,
  TextAnswer,
} from "./types";
import { ACTIVITY_TYPES, CHOICE_TYPES } from "./types";

/**
 * ═══ CE QUE LA RÉGIE PEUT FAIRE ═══
 *
 * Toutes ces routes sont fermées à qui n'a pas de rôle sur le domaine : le serveur
 * le vérifie, pas l'écran. Un participant qui les appellerait à la main reçoit 401,
 * ce qui n'est pas le cas en V1 — son `isPrivate` veut dire « connecté », pas
 * « organisateur », si bien que n'importe quel inscrit peut clore un sondage ou
 * effacer tous les votes de l'événement.
 */

export type ControlActivity = Activity & {
  answerCount: number;
  /**
   * Ses résultats, montrés ou non à la salle, pour les activités à choix ; `null`
   * pour les autres. Optionnel parce qu'une API plus ancienne ne les envoie pas.
   */
  results?: Results | null;
};

/* Sous `liveScope` : voir `useQuestions`. */
const questionsKey = (domainId: number, eventId: number) =>
  [...liveScope(domainId, eventId), "questions"] as const;

/**
 * La liste des activités préparées. Régie seulement : elle porte les bonnes réponses.
 *
 * ⚠️ LE CANAL DIT QUAND LA RELIRE, CE RAPPEL N'EST QU'UN FILET — même rôle que celui
 * du direct (#343). Elle ne se relisait qu'aux gestes de CETTE régie : à deux, l'autre
 * gardait la liste d'avant jusqu'au rechargement. Sans adresse de canal, ou pendant une
 * coupure, elle se rattrape en trente secondes au lieu de rester figée.
 */
export function useActivities() {
  const event = useEvent();

  return useQuery({
    queryKey: activitiesKey(event?.domainId ?? 0, event?.eventId ?? 0),
    queryFn: ({ signal }) =>
      apiFetch<ControlActivity[]>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/activities`,
        { api: "apix", signal },
      ),
    enabled: !!event,
    refetchInterval: 30_000,
    retry: false,
  });
}

/** Une question du public, et l'activité où elle a été posée. */
export type Question = TextAnswer & { activityId: number };

/**
 * TOUTES LES QUESTIONS DU PUBLIC DE L'ÉVÉNEMENT, pour l'écran de modération (#341).
 *
 * ⚠️ CELLES DE CHAQUE ACTIVITÉ « QUESTIONS », pas seulement de celle à l'écran : la
 * charge du direct ne porte que la seconde, et les questions d'une session passée ne
 * se relisaient plus dès que la suivante prenait l'écran.
 *
 * ⚠️ RANGÉE SOUS `liveScope`, EXPRÈS — l'inverse des SMS. Chaque geste de la régie
 * invalide le direct, et c'est ce qu'on veut ici : une question validée ou écartée
 * doit changer de pile aussitôt. Le coût reste borné, parce qu'une invalidation ne
 * relit que les lectures MONTÉES, et celle-ci ne l'est que sur l'écran de modération.
 * Les questions de l'activité à l'écran, elles, arrivent par le canal : voir
 * `withLiveQuestions`.
 */
export function useQuestions() {
  const event = useEvent();

  return useQuery({
    queryKey: questionsKey(event?.domainId ?? 0, event?.eventId ?? 0),
    queryFn: ({ signal }) =>
      apiFetch<Question[]>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/answers`,
        { api: "apix", signal },
      ),
    enabled: !!event,
    refetchInterval: 30_000,
    retry: false,
  });
}

/**
 * LES SMS REÇUS PAR CET ÉVÉNEMENT, et la réception ouverte ou non (voir `sms.ts`).
 *
 * ⚠️ LE CANAL LES POUSSE, CETTE LECTURE N'EST QU'UN FILET — même rôle que celle du
 * direct : sans adresse de canal, ou entre une coupure et la reconnexion, la liste se
 * rattrape toutes les trente secondes au lieu de se figer.
 */
export function useSmsInbox() {
  const event = useEvent();

  return useQuery({
    queryKey: smsKey(event?.domainId ?? 0, event?.eventId ?? 0),
    queryFn: ({ signal }) =>
      apiFetch<SmsInbox>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/sms`,
        { api: "apix", signal },
      ),
    enabled: !!event,
    refetchInterval: 30_000,
    retry: false,
  });
}

/**
 * UNE PAGE DE LA LISTE, au-delà de la première (voir `turnPage`) — rien sur la première,
 * qui est la liste en direct.
 *
 * ⚠️ PAS DE RELECTURE TOUTES LES TRENTE SECONDES : tenue par `upTo`, une page ne change
 * plus. Les SMS qui arrivent vont en première page.
 *
 * ⚠️ LA PAGE D'AVANT RESTE AFFICHÉE PENDANT QUE LA SUIVANTE ARRIVE, mais seulement sous
 * la même borne : repartie de la première page, une page tenue par une autre borne
 * montrerait un instant des SMS qui ne sont pas à leur place.
 */
export function useSmsPage(paging: Pick<SmsPaging, "page" | "upTo"> | null) {
  const event = useEvent();
  const page = paging?.page ?? 1;
  const upTo = paging?.upTo ?? 0;

  return useQuery({
    queryKey: smsPageKey(event?.domainId ?? 0, event?.eventId ?? 0, page, upTo),
    queryFn: ({ signal }) =>
      apiFetch<SmsPage>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/sms?page=${page}&upTo=${upTo}`,
        { api: "apix", signal },
      ),
    enabled: !!event && !!paging,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey.at(-1) === upTo ? previous : undefined,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * OUVRIR OU FERMER LA RÉCEPTION.
 *
 * ⚠️ `alsoClosed` DIT SI ELLE A ÉTÉ PRISE À UN AUTRE ÉVÉNEMENT. Un seul écoute le
 * numéro : l'ouvrir ici la ferme là-bas, et l'organisateur doit le savoir — sans son
 * nom, il peut appartenir à un autre client.
 */
export function useSetSmsReception() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (open: boolean) => {
      if (!event) throw new Error("Aucun événement");
      return apiFetch<SmsInbox & { alsoClosed: number }>(
        `/domains/${event.domainId}/events/${event.eventId}/live/sms`,
        { api: "apix", method: "PUT", body: { open } },
      );
    },
    onSuccess: ({ open, openedAt, total, messages }) => {
      if (!event) return;
      client.setQueryData<SmsInbox>(smsKey(event.domainId, event.eventId), {
        open, openedAt, total, messages,
      });
    },
  });
}

/**
 * REJOUER LES VOTES PAR SMS D'UNE ACTIVITÉ, sur une plage horaire (voir `sms.ts`).
 *
 * Mêmes caches que la remise à zéro, pour la même raison : la liste de la régie porte
 * le nombre de réponses, la charge du direct porte les résultats.
 *
 * ⚠️ RELUS AVANT DE RENDRE LA MAIN (#333). Le rejeu montre les résultats d'avant et
 * d'après : réussi mais relu plus tard, son « après » aurait d'abord affiché les
 * chiffres d'avant, un rejeu qui semblait n'avoir rien changé.
 */
export function useReplaySms() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: { activityId: number; from: string; to: string }) => {
      if (!event) throw new Error("Aucun événement");
      return apiFetch<SmsReplay>(
        `/domains/${event.domainId}/events/${event.eventId}/live/sms/replay`,
        { api: "apix", method: "POST", body },
      );
    },
    onSuccess: async () => {
      if (!event) return;
      await Promise.all([
        client.invalidateQueries({
          queryKey: activitiesKey(event.domainId, event.eventId),
        }),
        client.invalidateQueries({ queryKey: liveScope(event.domainId, event.eventId) }),
      ]);
    },
  });
}

/**
 * LES GESTES DE LA RÉGIE.
 *
 * ⚠️ CHAQUE APPEL RAFRAÎCHIT LES DEUX LISTES. Le direct et la liste des activités
 * décrivent la même réalité sous deux angles : ouvrir une activité change son état
 * dans la liste ET ce que voit la salle. N'en invalider qu'une laisse la console
 * afficher une activité fermée qui tourne.
 */
function useControlMutation<TBody, TResult = unknown>(
  path: (ids: { domainId: number; eventId: number }) => string,
  method: "PUT" | "POST" | "DELETE" = "PUT",
) {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: TBody) => {
      if (!event) throw new Error("Aucun événement");
      return apiFetch<TResult>(
        path({ domainId: event.domainId, eventId: event.eventId }),
        { api: "apix", method, body },
      );
    },
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({ queryKey: liveScope(event.domainId, event.eventId) });
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
    },
  });
}

/**
 * ⚠️ LA RÉPONSE PORTE `alsoClosed`. Ouvrir une activité clôt celles qui étaient
 * restées ouvertes — le serveur y tient l'invariant « une seule à la fois » — et
 * l'écran doit le DIRE : une clôture silencieuse se découvre plus tard, dans la
 * liste, sans qu'on sache qui l'a faite.
 */
export function useSetPhase(activityId: number | null) {
  return useControlMutation<
    {
      phase?: "draft" | "open" | "closed";
      showResults?: boolean;
      /**
       * ⚠️ TROIS VALEURS, PAS DEUX. `true` met l'activité à l'écran, `false` l'en
       * RETIRE, et l'omettre ne touche pas à l'écran. Un booléen qui ne saurait dire
       * que « mets-la » obligerait à un second appel pour la retirer, donc à un
       * intervalle où la phase est close et le module encore projeté.
       */
      makeActive?: boolean;
    },
    /* `removedFromScreen` dit si l'écran a VRAIMENT changé : terminer une activité
       qu'une autre avait déjà remplacée la marque sans rien retirer, ce qui est le
       cas courant du rangement d'après-session. La console n'a rien à en dire — le
       rangement se voit, la ligne change de groupe — mais le serveur, lui, doit
       pouvoir l'affirmer, et sa suite de tests le lit. */
    { alsoClosed: number; removedFromScreen: boolean; endedAt: string | null }
  >(
    ({ domainId, eventId }) =>
      `/domains/${domainId}/events/${eventId}/live/activities/${activityId}/phase`,
  );
}

export function useSetState() {
  return useControlMutation<Partial<LiveState>>(
    ({ domainId, eventId }) => `/domains/${domainId}/events/${eventId}/live/state`,
  );
}

export function useModerate(answerId: number | null) {
  return useControlMutation<{
    state?: "received" | "kept" | "discarded";
    spotlight?: boolean;
  }>(
    ({ domainId, eventId }) =>
      `/domains/${domainId}/events/${eventId}/live/answers/${answerId}`,
  );
}

/** Ce qu'on peut faire d'une activité, vu de la console. */
export function gesturesFor(
  activity: Pick<Activity, "phase" | "showResults" | "type" | "endedAt">,
) {
  /**
   * ═══ QUI A DES RÉSULTATS, ET QUI N'EN A PAS ═══
   *
   * ⚠️ TROIS TYPES SUR SIX N'EN ONT AUCUN, et `showResults` ne gouverne rien pour
   * eux. Vérifié dans le serveur, pas déduit : `serializeResults` est le seul endroit
   * où ce drapeau est lu, et il n'est appelé que pour les types à CHOIX
   * (`helpers/livePayload.js`). Le nuage de mots envoie ses mots et les questions du
   * public leurs questions quoi qu'il arrive — c'est même tout leur intérêt, un nuage
   * qui ne se voit pas ne sert à rien.
   *
   * Le bouton s'affichait quand même sur ces trois-là : il faisait un appel qui
   * réussissait, écrivait une colonne, et ne changeait rien à l'écran. Un contrôle
   * sans effet se lit comme un contrôle cassé, et l'organisateur reclique.
   *
   * ⚠️ LE CLASSEMENT ÉTAIT LE SEUL EXCLU (`WATCHED_TYPES`), parce qu'il est le seul à
   * ne rien se faire répondre du tout. C'était la bonne remarque sur le mauvais
   * critère : ce qui compte n'est pas « le public répond-il ? » mais « ce que le
   * public dit devient-il un résultat qu'on dévoile ? ».
   */
  const hasResults = CHOICE_TYPES.includes(activity.type);

  return {
    hasResults,
    canOpen: activity.phase !== "open",
    canClose: activity.phase === "open",
    canShow: hasResults && !activity.showResults,
    canHide: hasResults && activity.showResults,
    /**
     * ⚠️ TERMINER SE PROPOSE SUR TOUT CE QUI A SERVI, pas seulement sur ce qui est à
     * l'écran. C'était la première version, et elle laissait sans issue toutes les
     * activités qu'une suivante avait remplacées : closes, plus projetées, et
     * impossibles à ranger. Terminer les marque, et retire de l'écran par la même
     * occasion quand c'est bien elles qui l'occupent.
     *
     * ⚠️ SAUF UNE ACTIVITÉ PRÉPARÉE : elle n'a jamais servi, la terminer n'aurait
     * aucun sens et lui ferait quitter la liste de ce qui reste à faire.
     */
    canEnd: activity.phase !== "draft" && !activity.endedAt,
  };
}

/**
 * ═══ LES RÉSULTATS QU'UNE LIGNE DE LA RÉGIE AFFICHE ═══
 *
 * ⚠️ CEUX DU DIRECT POUR L'ACTIVITÉ À L'ÉCRAN, CEUX DE LA LISTE POUR LES AUTRES. La
 * liste ne se relit qu'aux gestes : ses nombres restaient figés pendant qu'une salle
 * votait. La charge du direct, elle, arrive à chaque réponse, et elle porte les
 * chiffres entiers à la régie, montrés ou non. Même règle que le compte de réponses,
 * pour la même raison : une seule activité est ouverte à la fois, donc une seule peut
 * voir ses nombres bouger.
 *
 * ⚠️ APPARIÉ SUR `live.activity`, PAS SUR `state.activityId`. Les résultats de la
 * charge sont ceux de l'activité qu'elle porte ; les deux ne divergent pas, mais c'est
 * le premier qui décrit à qui ces nombres appartiennent.
 */
export function rowResults(
  activity: Pick<ControlActivity, "id" | "results">,
  live: Pick<LivePayload, "activity" | "results"> | undefined,
): Results | null {
  if (live?.activity?.id === activity.id && live.results) return live.results;
  return activity.results ?? null;
}

/**
 * ═══ LES RÉSULTATS D'UNE LIGNE, DÉPLIÉS OU NON (#332) ═══
 *
 * ⚠️ DÉPLIÉS POUR L'ACTIVITÉ EN JEU, REPLIÉS POUR LES AUTRES. Tous repliés (#284), il
 * fallait un clic pour savoir où en est le vote qu'on vient de lancer ; tous dépliés
 * (#325), une session de dix activités devenait un mur de barres où la ligne qu'on
 * pilote se cherchait.
 *
 * ⚠️ EN JEU, C'EST OUVERTE OU ENCORE À L'ÉCRAN. On clôt une activité pour commenter
 * ses résultats : c'est le moment où on les lit, pas celui de les replier. Elle sort du
 * jeu quand une autre prend l'écran, ou qu'on la termine.
 */
export type RowPlay = { open: boolean; onScreen: boolean };

/** Le dépliant à l'arrivée sur la régie, ou au retour sur un groupe de la liste. */
export function unfoldedAtFirst(play: RowPlay): boolean {
  return play.open || play.onScreen;
}

/**
 * Le dépliant après un changement de la ligne. Il s'ouvre quand on ouvre l'activité
 * (« Lancer », « Rouvrir », ici ou depuis une autre régie), se replie quand elle sort
 * du jeu, et entre les deux garde le choix de celui qui pilote : un clic pour replier
 * ne doit pas être défait par la réponse suivante.
 */
export function unfoldedAfter(
  before: RowPlay,
  after: RowPlay,
  unfolded: boolean,
): boolean {
  if (after.open && !before.open) return true;
  if (unfoldedAtFirst(before) && !unfoldedAtFirst(after)) return false;
  return unfolded;
}

/** Un geste de phase, prêt à partir : son intitulé et la charge de la route. */
export type PhaseGesture = {
  key: "open" | "reopen" | "close" | "end";
  label: string;
  body: {
    phase?: "draft" | "open" | "closed";
    showResults?: boolean;
    makeActive?: boolean;
  };
};

/**
 * ═══ LE GESTE QU'ON VIENT FAIRE, ET LES AUTRES ═══
 *
 * ⚠️ LA LIGNE A PORTÉ SIX BOUTONS DE MÊME POIDS, dont un seul sert à l'instant où on
 * la regarde. Sur une console qu'on pilote sous pression, six cibles identiques ne se
 * lisent pas. La phase désigne le geste suivant sans ambiguïté : une activité préparée
 * se lance, une ouverte se clôt, une close se termine, une terminée se rouvre. Les
 * autres restent offerts, dans le menu.
 *
 * ⚠️ CONSTRUIT SUR `gesturesFor`, PAS À CÔTÉ. Deux listes de ce qui est possible
 * finissent par se contredire, et c'est la vue qui tranche au hasard : ici le menu ne
 * peut pas proposer ce que la règle interdit.
 */
export function phaseGestures(
  activity: Pick<Activity, "phase" | "showResults" | "type" | "endedAt">,
): { primary: PhaseGesture | undefined; others: PhaseGesture[] } {
  const can = gesturesFor(activity);
  const all: PhaseGesture[] = [];

  if (can.canOpen) {
    const reopening = activity.phase !== "draft";
    all.push({
      key: reopening ? "reopen" : "open",
      label: reopening ? "Rouvrir" : "Lancer",
      body: { phase: "open", makeActive: true },
    });
  }
  if (can.canClose) {
    all.push({ key: "close", label: "Clore", body: { phase: "closed" } });
  }
  if (can.canEnd) {
    all.push({
      key: "end",
      label: "Terminer",
      body: { phase: "closed", makeActive: false },
    });
  }

  /* Ce qu'on vient faire sur cette ligne, dans cet état. Le repli sur le premier
     couvre la préparée, dont le seul geste est de se lancer. */
  const wanted = activity.phase === "open"
    ? "close"
    : (activity.endedAt ? "reopen" : "end");
  const primary = all.find((g) => g.key === wanted) ?? all[0];

  return { primary, others: all.filter((g) => g !== primary) };
}

/**
 * ═══ LE CODE DE L'ÉCRAN DE SALLE ═══
 *
 * UN code par événement, sur la régie : on le génère, on le revoit à tout moment, on le
 * remplace, on le révoque. Le poste de la salle, qui n'est connecté à rien, le tape sur
 * `/ecran`. Il remplace les écrans à lien et à jeton.
 *
 * `code: null` : il n'y en a pas. `unreadable` : il y en a un, mais le serveur ne sait plus
 * le relire (clé changée) — il faut en générer un nouveau.
 */
export type ScreenCode = { code: string | null; createdAt: string | null; unreadable: boolean };

export function useScreenCode() {
  const event = useEvent();

  return useQuery({
    queryKey: screenCodeKey(event?.domainId ?? 0, event?.eventId ?? 0),
    queryFn: ({ signal }) =>
      apiFetch<ScreenCode>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/screen-code`,
        { api: "apix", signal },
      ),
    enabled: !!event,
    retry: false,
  });
}

/**
 * GÉNÉRER UN CODE (le premier, ou un nouveau qui remplace l'ancien) — `renew` — OU LE
 * RÉVOQUER. Remplacer ou révoquer coupe aussitôt les écrans ouverts avec l'ancien : c'est
 * le serveur qui le fait, et qui prévient les autres régies (`SCREEN_CODE`).
 */
function useScreenCodeGesture(method: "PUT" | "DELETE") {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<ScreenCode>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/screen-code`,
        { api: "apix", method },
      ),
    onSuccess: (data) => {
      if (!event) return;
      client.setQueryData(screenCodeKey(event.domainId, event.eventId), data);
    },
  });
}

export const useRenewScreenCode = () => useScreenCodeGesture("PUT");
export const useRevokeScreenCode = () => useScreenCodeGesture("DELETE");

/** Une activité qu'on prépare : le type ne change plus après, les réponses en dépendent. */
export type Draft = {
  type: Activity["type"];
  title: string;
  content: string;
  /** L'identifiant n'existe qu'à la MODIFICATION : c'est lui qui sauve les votes. */
  choices: { id?: number; label: string; points?: number; isRightAnswer?: boolean }[];
  /**
   * Les réglages du type. Le serveur applique ses propres défauts par-dessus ce
   * qu'on ne dit pas (`CONFIG_DEFAULTS` dans `helpers/live.js`) : n'envoyer que ce
   * que l'organisateur a réellement choisi.
   *
   * ⚠️ SAUF EN MODIFICATION, où le serveur REMPLACE le `config` entier : l'éditeur
   * renvoie alors celui qu'il a lu, avec ses changements (voir `withReveal`).
   */
  config?: Activity["config"];
};

/**
 * MODIFIER UNE ACTIVITÉ PRÉPARÉE.
 *
 * ⚠️ LES CHOIX PARTENT AVEC LEUR IDENTIFIANT. Le serveur les APPARIE par id plutôt
 * que de les remplacer en bloc : `LiveAnswers.choiceId` cascade à la suppression, donc
 * renvoyer des choix sans identifiant effacerait tous les votes déjà exprimés à la
 * première correction de faute de frappe. Un choix qu'on retire alors qu'il porte des
 * réponses est refusé, et nommé.
 *
 * ⚠️ NI LE TYPE NI LA PHASE. Le type est structurel (les réponses enregistrées
 * supposent la forme de l'activité) et la phase a sa propre route, avec ses effets de
 * bord — ouvrir clôt les autres.
 */
export function useUpdateActivity(id: number) {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ title, content, choices, config }: Draft) =>
      apiFetch<Activity>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/activities/${id}`,
        {
          api: "apix",
          method: "PUT",
          body: { activity: { title, content, config }, choices },
        },
      ),
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
      /* La charge du direct porte l'activité à l'écran : un libellé corrigé doit
         arriver aux téléphones, pas seulement à la liste de la régie. */
      void client.invalidateQueries({ queryKey: liveScope(event.domainId, event.eventId) });
    },
  });
}

export function useCreateActivity() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ type, title, content, choices, config }: Draft) =>
      apiFetch<ControlActivity>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/activities`,
        {
          api: "apix",
          method: "POST",
          body: { activity: { type, title, content, config }, choices },
        },
      ),
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
    },
  });
}

/**
 * SUPPRIMER UNE ACTIVITÉ.
 *
 * ⚠️ `force` EST EXIGÉ PAR LE SERVEUR dès qu'elle contient des réponses : la
 * suppression emporte les choix ET les votes. L'écran doit donc demander, une fois,
 * plutôt que d'envoyer `force` d'office — le bouton est voisin des autres et la
 * régie se pilote sous pression.
 */
export function useRemoveActivity() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      apiFetch(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/activities/${id}`,
        { api: "apix", method: "DELETE", body: force ? { force: true } : {} },
      ),
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
      void client.invalidateQueries({ queryKey: liveScope(event.domainId, event.eventId) });
    },
  });
}

/**
 * UNE ACTIVITÉ A-T-ELLE ÉTÉ JOUÉE ? Des réponses, une phase qui n'est plus « préparée »,
 * des résultats montrés, une fin posée : il y a quelque chose à remettre à zéro. Sur une
 * activité vierge, « Réinitialiser » ne ferait rien — l'offrir donnerait à croire qu'il
 * manque quelque chose.
 */
export function hasBeenPlayed(
  activity: Pick<Activity, "phase" | "showResults" | "endedAt">,
  answers: number,
): boolean {
  return answers > 0 || activity.phase !== "draft" || !!activity.showResults || !!activity.endedAt;
}

/**
 * CE QUE « RÉINITIALISER LA RÉPÉTITION » TOUCHERAIT : les activités jouées, et combien de
 * réponses partiraient — le compte de la charge du direct pour celle à l'écran (plus frais
 * que la liste, voir `liveCount`), celui de la liste pour les autres. `needed` : il y a quelque
 * chose à remettre à zéro, une activité jouée ou une diffusion qui n'est plus « Avant ».
 */
export function rehearsalOf(
  activities: ReadonlyArray<ControlActivity>,
  live?: Pick<LivePayload, "state" | "answerCount"> | null,
): { played: number; answers: number; needed: boolean } {
  const countOf = (a: ControlActivity) =>
    live?.state.activityId === a.id && typeof live.answerCount === "number"
      ? live.answerCount
      : a.answerCount;
  const played = activities.filter((a) => hasBeenPlayed(a, countOf(a))).length;
  const answers = activities.reduce((total, a) => total + countOf(a), 0);
  return { played, answers, needed: played > 0 || (live?.state.broadcast ?? "before") !== "before" };
}

/**
 * ═══ REMETTRE À ZÉRO APRÈS UNE RÉPÉTITION, SANS RIEN SUPPRIMER ═══
 *
 * « On fait des dry run » (2026-09-30) : on joue les activités devant l'équipe, puis tout
 * revient comme avant l'arrivée de la salle. Une activité (`activityId`), ou tout le direct
 * (sans argument) : les réponses sont effacées, votes SMS compris, et les activités
 * redeviennent préparées — fermées, résultats masqués, pas terminées, retirées de l'écran.
 * Tout le direct revient en plus à « Avant ». Voir `api/src/routes/live/reset.js`.
 *
 * ⚠️ IL REMPLACE « VIDER LES RÉPONSES », qui laissait l'activité close, ses résultats
 * montrés, parfois à l'écran — et le serveur refuse de redire « préparée » une activité
 * qui a eu des réponses. Vider sans remettre à zéro ne servait pas la répétition.
 *
 * Les deux caches sont invalidés : la liste de la régie porte le nombre de réponses et
 * la phase, la charge du direct porte l'écran et les résultats.
 */
export function useResetLive() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (activityId?: number) =>
      apiFetch<{ activities: number; deletedAnswers: number }>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/reset`,
        { api: "apix", method: "POST", body: activityId ? { activityId } : {} },
      ),
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
      void client.invalidateQueries({ queryKey: liveScope(event.domainId, event.eventId) });
    },
  });
}

/**
 * RANGER LES ACTIVITÉS.
 *
 * ⚠️ ON ENVOIE LA LISTE ENTIÈRE, pas « monte celle-ci d'un cran ». Deux organisateurs
 * qui bougent deux lignes en même temps produiraient sinon un ordre qu'aucun des deux
 * n'a voulu ; ici, ce qui part EST l'ordre voulu, et le dernier qui écrit gagne en
 * entier. Le serveur refuse d'ailleurs une liste incomplète.
 *
 * ⚠️ ET L'ORDRE NE SE DIFFUSE PAS : c'est une commodité de la régie, il ne change ni
 * ce qui est à l'écran ni ce qu'une salle voit. Seule la liste des activités est
 * relue, pas la charge du direct.
 */
export function useReorderActivities() {
  const event = useEvent();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetch<{ ids: number[] }>(
        `/domains/${event!.domainId}/events/${event!.eventId}/live/activities/order`,
        { api: "apix", method: "PUT", body: { ids } },
      ),
    onSuccess: () => {
      if (!event) return;
      void client.invalidateQueries({
        queryKey: activitiesKey(event.domainId, event.eventId),
      });
    },
  });
}

/**
 * LA LISTE, UNE FOIS UNE LIGNE DÉPLACÉE D'UN CRAN.
 *
 * Sortie à part pour être vérifiable : c'est la seule règle de tout ce geste, et elle
 * doit refuser silencieusement de sortir des bornes plutôt que de rendre une liste
 * amputée.
 */
export function movedBy(ids: number[], id: number, sens: -1 | 1): number[] {
  return movedAmong(ids, ids, id, sens);
}

/**
 * LA MÊME CHOSE, QUAND LA LISTE EST FILTRÉE.
 *
 * ⚠️ ON ÉCHANGE AVEC LA LIGNE VISIBLE VOISINE, pas avec la voisine dans l'ordre
 * complet. Sous un filtre, ces deux lignes ne sont pas la même : une activité rangée
 * juste au-dessus mais TERMINÉE est masquée, donc « monter » échangeait avec elle et
 * ne changeait rien à ce qu'on regarde. Une commande dont l'effet est invisible se
 * relit comme une commande cassée, et l'organisateur reclique.
 *
 * ⚠️ ET C'EST L'ORDRE COMPLET QUI PART AU SERVEUR. Il refuse une liste incomplète, à
 * raison : envoyer le seul sous-ensemble visible effacerait la place des autres.
 */
export function movedAmong(
  ids: number[],
  visible: number[],
  id: number,
  sens: -1 | 1,
): number[] {
  const from = visible.indexOf(id);
  const toward = from + sens;
  if (from < 0 || toward < 0 || toward >= visible.length) return ids;

  const partner = visible[toward]!;
  return swapped(ids, ids.indexOf(id), ids.indexOf(partner));
}

/**
 * DEUX ÉLÉMENTS ÉCHANGÉS, et rien si l'un des deux n'existe pas.
 *
 * ⚠️ IL REND UNE COPIE, TOUJOURS. Muter la liste en place ne changerait pas sa
 * référence : React ne verrait rien à rendre, et la flèche passerait pour morte.
 */
function swapped<T>(list: ReadonlyArray<T>, a: number, b: number): T[] {
  if (a < 0 || b < 0 || a >= list.length || b >= list.length) return [...list];
  const next = [...list];
  next[a] = list[b]!;
  next[b] = list[a]!;
  return next;
}

/**
 * UNE LIGNE DÉPLACÉE D'UN CRAN DANS SA LISTE, quelle que soit la liste.
 *
 * ⚠️ L'ORDRE DES RÉPONSES EST CELUI DU TABLEAU ENVOYÉ. Le serveur écrit la position
 * de chaque choix depuis son rang dans la charge (`activities/update.js`), donc
 * réordonner ici SUFFIT à réordonner en base — et à réordonner ce que la salle voit,
 * puisque la lecture trie sur cette position.
 *
 * ⚠️ ET IL REFUSE SILENCIEUSEMENT AUX BORNES. Rendre une liste amputée ferait
 * disparaître une réponse pour avoir cliqué sur la flèche du haut.
 */
export function moved<T>(
  list: ReadonlyArray<T>,
  index: number,
  sens: -1 | 1,
): T[] {
  return swapped(list, index, index + sens);
}

/**
 * ═══ TERMINÉE, OU PAS ENCORE ═══
 *
 * ⚠️ ON LIT LE FAIT, ON NE LE DÉDUIT PLUS. Cette règle a d'abord été « close et plus à
 * l'écran », et elle était fausse dans le cas le plus courant d'une session : on lance
 * une activité, on la clôt pour commenter ses résultats, on en lance une autre — et la
 * première était étiquetée terminée sans que personne ne l'ait terminée. Elle
 * attendait peut-être d'être rouverte, et elle avait quitté le groupe où on la
 * cherchait.
 *
 * Seul le geste « Terminer » pose `endedAt`, et remettre l'activité en jeu l'efface.
 */
export function isDone(activity: Pick<Activity, "endedAt">): boolean {
  return !!activity.endedAt;
}

/**
 * LES TYPES QUE CET ÉVÉNEMENT PORTE RÉELLEMENT, dans l'ordre du produit.
 *
 * ⚠️ PAS LES SIX TYPES D'OFFICE. Un sélecteur qui propose « Nuage de mots » à un
 * événement qui n'en a aucun rend une liste vide : on lit une console cassée là où
 * il n'y avait rien à voir. Ce qu'on propose est ce qui existe.
 */
export function typesPresent<T extends Pick<Activity, "type">>(
  activities: ReadonlyArray<T>,
): ActivityType[] {
  const seen = new Set(activities.map((a) => a.type));
  return ACTIVITY_TYPES.filter((type) => seen.has(type));
}

/**
 * LA LISTE RESTREINTE À UN TYPE, ou telle quelle sans filtre.
 *
 * ⚠️ APPLIQUÉ AVANT LE PARTAGE EN DEUX GROUPES, pas après : les comptes des onglets
 * doivent décrire ce qu'on verrait en cliquant dessus. Filtré après, « Terminées (4) »
 * annonçait quatre lignes et n'en montrait qu'une.
 */
export function ofType<T extends Pick<Activity, "type">>(
  activities: ReadonlyArray<T>,
  type: ActivityType | "",
): T[] {
  return type ? activities.filter((a) => a.type === type) : [...activities];
}

/**
 * LE TYPE FILTRÉ, RAMENÉ À CE QUE LA COMMANDE PEUT ENCORE EXPRIMER.
 *
 * ⚠️ UN FILTRE DOIT POUVOIR SE LEVER, ET SON SEUL LEVIER EST LE `<select>` — qui ne
 * se dessine qu'au-dessus de deux types (voir `typesPresent`). Supprimer la dernière
 * activité du type regardé emportait donc la commande avec elle : la liste restait
 * filtrée sur un type que l'événement ne porte plus, donc vide, sans plus rien pour
 * revenir à « Tous ». Seul un rechargement de la page en sortait.
 *
 * Le cas plus doux, quand il reste deux types, n'était pas meilleur : le `<select>`
 * contrôlé portait une valeur sans `<option>` correspondante, et s'affichait vide.
 */
export function offeredType(
  type: ActivityType | "",
  types: ReadonlyArray<ActivityType>,
): ActivityType | "" {
  return types.length > 1 && type && types.includes(type) ? type : "";
}

/** Les deux groupes de la liste de régie, dans l'ordre rangé. */
export function splitActivities<T extends Pick<Activity, "endedAt">>(
  activities: ReadonlyArray<T>,
): { todo: T[]; done: T[] } {
  const todo: T[] = [];
  const done: T[] = [];
  for (const activity of activities) {
    (isDone(activity) ? done : todo).push(activity);
  }
  return { todo, done };
}
